import type { Connection, CreateStakeAccountParams, DelegateStakeParams } from "@solana/web3.js";
import { Authorized, Keypair, Lockup, PublicKey, StakeProgram } from "@solana/web3.js";
import { NATIVE_SOL, toLamports } from "@stepfinance/common";
import type { Dispatch, SetStateAction } from "react";

import { TokenChangeNotificationDisplay } from "../../components/Notifications/TokenChangeNotificationDisplay";
import { confirmOptions } from "../../constants/transaction";
import type { StakeNotificationStrings } from "../../contexts/language/languageTypes";
import type { ConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { sendTransaction } from "../../utils/transactions";
import { formatStringWithPlaceholders } from "../../utils/utils";

export const STEP_VALIDATOR_VOTE_ACCOUNT = new PublicKey("StepeLdhJ2znRjHcZdjwMWsC4nTRURNKQY8Nca82LJp");

export const validatorStake = async (
  wallet: ConnectedWallet,
  connection: Connection,
  amount: number,
  onTransactionComplete: Dispatch<SetStateAction<boolean>>,
  notificationStrings: StakeNotificationStrings,
) => {
  const amountToStakeLamports = toLamports(amount, 9);

  const minimumAmount = await connection.getMinimumBalanceForRentExemption(StakeProgram.space);
  const fromPubkey = wallet.publicKey;
  const newStakeAccount = new Keypair();
  const authorizedPubkey = wallet.publicKey;
  const authorized = new Authorized(authorizedPubkey, authorizedPubkey);
  const lockup = new Lockup(0, 0, fromPubkey);
  const lamports = minimumAmount + amountToStakeLamports;
  const stakeAccountIns: CreateStakeAccountParams = {
    fromPubkey,
    stakePubkey: newStakeAccount.publicKey,
    authorized,
    lockup,
    lamports,
  };
  const newStakeAccountIns = StakeProgram.createAccount(stakeAccountIns);

  const instruction: DelegateStakeParams = {
    stakePubkey: newStakeAccount.publicKey,
    authorizedPubkey: new PublicKey(wallet.publicKey),
    votePubkey: STEP_VALIDATOR_VOTE_ACCOUNT,
  };

  const stakeIXs = newStakeAccountIns.instructions.concat(StakeProgram.delegate(instruction).instructions);

  return sendTransaction(
    connection,
    wallet,
    stakeIXs,
    [newStakeAccount],
    true,
    confirmOptions,
    true,
    {
      approveMessage: notificationStrings.approveTransaction,
      inProgress: {
        message: formatStringWithPlaceholders(
          notificationStrings.youAreDoingAction.message,
          notificationStrings.youAreDoingAction.staked,
          "SOL",
        ),
        description: notificationStrings.transactionInProgress,
      },
      success: {
        message: notificationStrings.stakeSuccessful,
        description: (
          <>
            <span>
              {formatStringWithPlaceholders(
                notificationStrings.youDidAction.message,
                notificationStrings.youDidAction.staked,
              )}
            </span>
            <TokenChangeNotificationDisplay amount={amount} mintAddress={NATIVE_SOL.toBase58()} type={"increase"} />
          </>
        ),
      },
    },
    () => onTransactionComplete(true),
  );
};
